// src/API/chatService.js
import axiosInstance from "./axiosInstance";
import { decryptText } from "../config/encryption";

export const chatService = {
  // Get chat history with pagination
  getChatHistory: async (pageNumber = 1, pageSize = 50) => {
    try {
      const response = await axiosInstance.get(
        `/Chat/history?pageNumber=${pageNumber}&pageSize=${pageSize}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching chat history:", error);
      throw error;
    }
  },

  // Get current user info
  getUserInfo: async () => {
    try {
      const response = await axiosInstance.get("/Chat/user-info");
      return response.data;
    } catch (error) {
      console.error("Error fetching user info:", error);
      throw error;
    }
  },

  // Get online users (with optional pagination)
  getOnlineUsers: async (pageNumber = null, pageSize = null) => {
    try {
      let url = "/Chat/online-users";
      if (pageNumber !== null && pageSize !== null) {
        url += `?pageNumber=${pageNumber}&pageSize=${pageSize}`;
      }
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error("Error fetching online users:", error);
      throw error;
    }
  },

  // Get online users count
  getOnlineUsersCount: async () => {
    try {
      const response = await axiosInstance.get("/Chat/online-users/count");
      return response.data;
    } catch (error) {
      console.error("Error fetching online users count:", error);
      throw error;
    }
  },

  // Check if specific user is online
  checkUserOnline: async (studentId) => {
    try {
      const response = await axiosInstance.get(
        `/Chat/online-users/${studentId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error checking user online status:", error);
      throw error;
    }
  },

  // Get typing users
  getTypingUsers: async () => {
    try {
      const response = await axiosInstance.get("/Chat/typing-users");
      return response.data;
    } catch (error) {
      console.error("Error fetching typing users:", error);
      throw error;
    }
  },

  // Get chat statistics
  getChatStats: async () => {
    try {
      const response = await axiosInstance.get("/Chat/stats");
      return response.data;
    } catch (error) {
      console.error("Error fetching chat stats:", error);
      throw error;
    }
  },

  // Delete a message
  deleteMessage: async (messageId) => {
    try {
      console.log("Sending delete request for messageId:", messageId);
      const response = await axiosInstance.delete(`/Chat/message/${messageId}`);
      console.log("Delete response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error deleting message:", error);
      console.error("Error details:", error.response?.data);
      throw error;
    }
  },

  // Update/edit a message
  updateMessage: async (messageId, newMessage) => {
    try {
      console.log("Sending update request:", { messageId, newMessage });
      const response = await axiosInstance.put(`/Chat/message/${messageId}`, {
        Message: newMessage, // Backend expects capital 'M' in Message
      });
      console.log("Update response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error updating message:", error);
      console.error("Error details:", error.response?.data);
      throw error;
    }
  },

  // Upload image for chat message
  uploadImage: async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axiosInstance.post("/Chat/upload-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  },

  // Upload voice/audio for chat message
  uploadVoice: async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axiosInstance.post("/Chat/upload-voice", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error uploading voice:", error);
      throw error;
    }
  },

  // Get JWT token for SignalR connection (supports both user and admin sessions)
  getToken: () => {
    // Try user session first
    const user = localStorage.getItem("UserLogDetails");
    if (user) {
      try {
        const decrypted = decryptText(user);
        const { Token } = JSON.parse(decrypted);
        return Token;
      } catch (e) {
        console.error("Token parse error:", e);
      }
    }
    
    // Try admin session
    const adminSession = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
    if (adminSession) {
      try {
        const decrypted = decryptText(adminSession);
        const { Token } = JSON.parse(decrypted);
        return Token;
      } catch (e) {
        console.error("Admin token parse error:", e);
      }
    }
    
    return null;
  },
  
  // Get admin token specifically (for admin context)
  getAdminToken: () => {
    const adminSession = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
    if (adminSession) {
      try {
        const decrypted = decryptText(adminSession);
        const { Token } = JSON.parse(decrypted);
        return Token;
      } catch (e) {
        console.error("Admin token parse error:", e);
        return null;
      }
    }
    return null;
  },
};

export default chatService;

